import type { TFunction } from "i18next";

import type { ApprovalKind, RowOutcome, RowPath } from "@/api/agent-conversation";
import type { NoticeCode, SummaryItem, TerminalActivity } from "@/components/agents/conversation/types";

/*
 * Os textos fixos da conversa, traduzidos na tela pelo código: o agente
 * responde no idioma da última mensagem do consultor, mas o que é fixo — recusa,
 * aviso do sistema, rótulo — segue o idioma da conta.
 *
 * Cada código tem o seu `t("…")` literal num `switch`, como em
 * `warningMessages.ts`: os gates de i18n acham as chaves por regex no fonte, e uma
 * tabela consultada em tempo de execução ficaria invisível para eles.
 */

/** Recusa do agente. O código vem do servidor já com o prefixo (`refusal.off_topic`). */
export function refusalText(t: TFunction, code: string): string {
  switch (code) {
    case "refusal.off_topic":
      return t("refusal.off_topic");
    default:
      // Código novo no servidor sem chave aqui: frase genérica, nunca o código cru.
      return t("refusal.generic");
  }
}

export function noticeText(t: TFunction, code: NoticeCode, params: Record<string, string>): string {
  switch (code) {
    case "session_paused_idle":
      return t(
        "The session was paused after a while without activity. Nothing unapproved was saved. Send a message to resume.",
      );
    case "session_restarted":
      return t("The service restarted. Send a message to resume where the agent stopped.");
    case "turn_interrupted":
      return t("Stopped. The browser stays open on the same screen.");
    case "turn_failed":
      return t("The agent's step failed. Send a message to tell it how to continue.");
    case "driver_error": {
      const text = failureText(t, params.code || params.detail || "");
      // A pausa por ID recusado leva a mensagem do SAP entre parênteses.
      if (ID_PAUSE_CODES.has(params.code) && params.detail && params.detail !== params.code) {
        return `${text} (${params.detail})`;
      }
      if (params.code === "sap_registration_refused" && params.detail) {
        return t(
          "Registration failed: {{message}}. Go to the Management stage, fix the unit data and run it again.",
          { message: params.detail },
        );
      }
      return text;
    }
    case "login_failed":
      if (!params.detail?.trim()) return t("Could not sign in to the Configuration tenant.");
      if (isFailureCode(params.detail)) return failureText(t, params.detail);
      return t("Could not sign in to the Configuration tenant: {{detail}}", {
        detail: params.detail ?? "",
      });
    case "target_read_only":
      if (!params.reason?.trim()) return t("The configuration screen opened in display-only mode.");
      return t("The configuration screen opened in display-only mode: {{reason}}", {
        reason: params.reason ?? "",
      });
    case "script_deviation":
      if (!params.step) return t("The script did not finish the unit. The agent took over in the chat.");
      return t("The script stopped at step {{step}}. The agent took over in the chat.", {
        step: params.step,
      });
    case "script_recorded":
      return t("Script {{code}} saved (version {{version}}). The next rows run without the model.", {
        code: params.code ?? "",
        version: params.version ?? "",
      });
    case "conversation_completed":
      return t("Conversation completed.");
    case "conversation_interrupted":
      return t("Conversation ended before all rows were done.");
    case "conversation_failed":
      return t("Conversation failed.");
    default: {
      const unreachable: never = code;
      return unreachable;
    }
  }
}

export const ID_PAUSE_CODES: ReadonlySet<string> = new Set([
  "unit_id_edit_required",
  "unit_id_all_refused",
  "company_id_edit_required",
  "company_id_all_refused",
]);

function isFailureCode(code: string): boolean {
  return code.startsWith("module_config.") || code.startsWith("login_not_confirmed") || [
    "login_timeout", "login_agent_failed", "cbc_connection_not_configured",
    "configuration_login_not_configured", "webgui_not_loaded",
    "launchpad_home_unreachable", "navigation_screen_unreadable", "session_lost",
    "navigation_agent_failed", "navigation_timeout", "navigation_deadline",
    "navigation_guidance_exhausted", "workspace_not_found", "workspace_switch_failed",
    "unit_id_edit_required", "unit_id_all_refused", "company_id_edit_required",
    "company_id_all_refused",
  ].includes(code);
}

function failureText(t: TFunction, code: string): string {
  switch (code) {
    case "login_not_confirmed:invalid_input":
      return t("Login could not be confirmed: invalid input.");
    case "login_timeout":
    case "TimeoutError":
      return t("The operation timed out. Send a message to try again.");
    case "login_agent_failed":
      return t("The login agent failed. Check the connection and try again.");
    case "cbc_connection_not_configured":
      return t("Set up and test your CBC in Connections.");
    case "configuration_login_not_configured":
      return t("Set your Configuration user and password in Connections, or use the same CBC user and password.");
    case "webgui_not_loaded":
      return t("The SAP screen did not load.");
    case "sap_registration_refused":
      return t("Registration failed: SAP refused it. Go to the Management stage, fix the unit data and run it again.");
    case "unit_id_edit_required":
      return t("SAP refused the ID of this unit. Go to the Management stage, edit the ID to one SAP accepts and send a message here to continue. The session stays open for 5 minutes.");
    case "unit_id_all_refused":
      return t("SAP refused both the original ID and the edited ID of this unit. Go to the Management stage, edit the ID again to one SAP accepts and send a message here to continue. The session stays open for 5 minutes.");
    case "company_id_edit_required":
      return t("SAP refused the ID of the company of this unit. Go to the Management stage, edit the ID of the company to one SAP accepts and send a message here to continue. The session stays open for 5 minutes.");
    case "company_id_all_refused":
      return t("SAP refused both the original ID and the edited ID of the company of this unit. Go to the Management stage, edit the ID of the company again to one SAP accepts and send a message here to continue. The session stays open for 5 minutes.");
    case "workspace_not_found":
      return t("Execution stopped: the CBC workspace saved in your connection was not found in the CBC workspace list. Enter the workspace name exactly as it appears in the CBC, test it there and run again.");
    case "workspace_switch_failed":
      return t("Execution stopped: the agent could not switch the CBC to the workspace saved in your connection. Check the workspace name in Connections and run again.");
    case "session_closing":
      return t("The session is closing. Try again shortly.");
    case "conversation_close_incomplete":
      return t("The session could not be fully closed.");
    case "row_plan_changed":
      return t("The approved row plan changed. Review the rows before continuing.");
    case "recovery_requires_discard":
      return t("Recovery requires approval to discard unsaved fields.");
    case "recovery_open_failed":
      return t("The browser could not be reopened for recovery.");
    case "module_config.screen_unavailable":
    case "navigation_screen_unreadable":
      return t("The SAP screen could not be read. Check the connection and try again.");
    case "module_config.session_expired":
    case "session_lost":
      return t("The SAP session expired. Sign in again to continue.");
    case "module_config.preparation_invalid_input":
      return t("The app could not be prepared with the supplied values. Review the mapping and try again.");
    case "launchpad_home_unreachable":
      return t("The SAP launchpad home could not be reached. Check the tenant connection and try again.");
    case "navigation_agent_failed":
      return t("The navigation agent failed before opening the app. Try again.");
    case "navigation_timeout":
    case "navigation_deadline":
      return t("Navigation timed out before opening the app. Try again.");
    case "module_config.unsupported_technology":
      return t("This SAP screen technology is not supported for module configuration.");
    case "module_config.not_at_start_screen":
      return t("The app is not at its starting screen. Return to the app entry and try again.");
    case "module_config.preparation_refused":
      return t("The app could not be prepared safely. Check the SAP screen and try again.");
    case "module_config.draft_open":
      return t("There is an open draft in the app; approve discarding it to continue.");
    case "navigation_guidance_exhausted":
      return t(
        "Could not open the configuration from the launchpad search after 3 attempts with your guidance. Open it in SAP and check its name, then try again.",
      );
    default:
      if (code.startsWith("login_not_confirmed:")) {
        // O login não passou (SAP fora do ar ou lento, senha trocada ou expirada): tentar de
        // novo daqui a pouco; a credencial é de quem apertou o play.
        return t("Could not sign in to the CBC right now. Try again in a few minutes; if it keeps failing, check your credentials in Connections.");
      }
      return code.trim()
        ? t("The conversation could not continue ({{code}}).", { code })
        : t("The conversation could not continue.");
  }
}

/**
 * Resultado de uma linha do recorte. `rowIndex` é a posição da linha no de-para,
 * a partir de 0 (`MappingRow.row_index`); a tela conta a partir de 1.
 */
export function rowText(t: TFunction, outcome: RowOutcome, rowIndex: number): string {
  const row = rowIndex + 1;
  switch (outcome) {
    case "created":
      return t("Row {{row}}: created", { row });
    case "already_existed":
      return t("Row {{row}}: already existed", { row });
    case "failed":
      return t("Row {{row}}: failed", { row });
    case "skipped":
      return t("Row {{row}}: skipped", { row });
    default: {
      const unreachable: never = outcome;
      return unreachable;
    }
  }
}

export function rowPathText(t: TFunction, path: RowPath): string {
  return path === "script" ? t("by script") : t("in conversation");
}

export function approvalTitle(t: TFunction, kind: ApprovalKind): string {
  switch (kind) {
    case "row_plan":
      return t("Rows to run");
    case "save":
      return t("Save this row");
    case "script":
      return t("Script for the next rows");
    case "continue_rows":
      return t("Continue remaining rows");
    case "discard":
      return t("Leave without saving");
    default: {
      const unreachable: never = kind;
      return unreachable;
    }
  }
}

/**
 * O corpo do cartão de aprovação. O `summary` que o modelo escreve já vem no idioma
 * da conversa e passa como está. O das aprovações que o código pede é
 * traduzido aqui: o do condutor vem como código, e o do `discard` é uma frase fixa
 * em inglês, que a tela troca pela dela.
 */
export function approvalSummaryText(t: TFunction, kind: ApprovalKind, summary: string): string {
  if (kind === "discard") {
    return t("The fields filled in and not saved are discarded when the agent leaves the transaction.");
  }
  switch (summary) {
    case "approval.confirm_first_scripted_row":
      return t("approval.confirm_first_scripted_row");
    case "approval.continue_rows":
      return t("approval.continue_rows");
    default:
      return summary;
  }
}

/** Resumo do encerramento (spec §7.1, fase 4), montado das linhas, sem o modelo. */
export function summaryText(
  t: TFunction,
  counts: Pick<SummaryItem, "created" | "alreadyExisted" | "failed" | "skipped">,
): string {
  return t(
    "Summary — created: {{created}} · already existed: {{existed}} · failed: {{failed}} · skipped: {{skipped}}",
    {
      created: counts.created,
      existed: counts.alreadyExisted,
      failed: counts.failed,
      skipped: counts.skipped,
    },
  );
}

export function activityText(t: TFunction, activity: TerminalActivity): string {
  switch (activity) {
    case "thinking":
      return t("Thinking…");
    case "acting":
      return t("Working on the SAP screen…");
    case "waiting":
      return t("Waiting for you");
    case "awaiting_rerun":
      return t("Waiting to run again");
    default: {
      const unreachable: never = activity;
      return unreachable;
    }
  }
}


/** A recusa mantém o código nos dados; a interface mostra apenas uma razão traduzida. */
export function conversationErrorText(t: TFunction, code: string | null): string {
  switch (code) {
    case "conversation_closed": return t("This conversation is closed. Open a new conversation to continue.");
    // Nó 02: a unidade já não espera a resposta do ID (outra aba respondeu, ou ela rodou).
    case "unit_not_awaiting": return t("The unit no longer awaits an answer.");
    case "session_closing": return t("The session is closing. Wait a moment, then retry once to continue.");
    case "message_too_long": return t("Message must have at most 4000 characters.");
    case "access_denied": return t("You do not have permission to access this conversation.");
    case "conversation_not_found": return t("This conversation could not be found.");
    case "invalid_message": return t("This message was refused. Review its content before sending again.");
    case "connection_failed": return t("Connection failed. Try again.");
    case "service_unavailable": return t("The conversation service is unavailable (503). Try again shortly.");
    case "gateway_failed": return t("The gateway could not reach the conversation service (502). Try again.");
    case "network_failed": return t("The server could not be reached. Check your network connection and try again.");
    case "run_owned_by_another_user": return t("Only the person who started this execution can answer or stop it.");
    default: return t("The request was refused. Check your access and message before continuing.");
  }
}


/**
 * As frases de progresso do console (o ``message`` dos eventos). O servidor manda a frase
 * canônica em inglês, que é a chave do i18n: as fixas se traduzem direto; as que levam um
 * valor (a unidade, o workspace) casam por padrão e entram na tradução com o valor.
 */
const PROGRESS_PATTERNS: ReadonlyArray<readonly [RegExp, string, readonly string[]]> = [
  [/^Selected the company (.+) from the list$/, "Selected the company {{id}} from the list", ["id"]],
  [/^Starting the registration: (.+)$/, "Starting the registration: {{unit}}", ["unit"]],
  [/^Analyzing (.+)$/, "Analyzing {{unit}}", ["unit"]],
  [/^Running (.+)$/, "Running {{action}}", ["action"]],
  [/^Did not work: (.+)$/, "Did not work: {{action}}", ["action"]],
  [/^Ran the action (.+)$/, "Ran the action {{action}}", ["action"]],
  [/^Clicked (?!Create )(.+)$/, "Clicked {{name}}", ["name"]],
  [/^Opened the (.+) form$/, "Opened the {{type}} form", ["type"]],
  [/^Expanded the (.+?) (\S+) in the table$/, "Expanded the {{kind}} {{id}} in the table", ["kind", "id"]],
  [/^Clicked Create (.+?) under the (.+)$/, "Clicked Create {{label}} under the {{parent}}", ["label", "parent"]],
  [/^Used the top Create button to create the (.+)$/, "Used the top Create button to create the {{type}}", ["type"]],
  [/^Checked the CBC workspace: it was already on (.+)$/, "Checked the CBC workspace: it was already on {{name}}", ["name"]],
  [/^The CBC was on workspace (.+?): switching to (.+)$/, "The CBC was on workspace {{now}}: switching to {{name}}", ["now", "name"]],
  [/^The workspace (.+) is not in the CBC list$/, "The workspace {{name}} is not in the CBC list", ["name"]],
  [/^Switched the CBC workspace to (.+)$/, "Switched the CBC workspace to {{name}}", ["name"]],
];

export function progressLineText(t: TFunction, message: string): string {
  for (const [pattern, key, names] of PROGRESS_PATTERNS) {
    const found = pattern.exec(message);
    if (found) {
      return t(key, Object.fromEntries(names.map((name, index) => [name, found[index + 1]])));
    }
  }
  return t(message, { defaultValue: message });
}

// Colunas do de-para da unidade (Nó 02) em palavras: o chat nunca mostra `col:UNIT.ID`.
const UNIT_COLUMN_LABELS: Record<string, string> = {
  ID: "Unit ID",
  NAME: "Name",
  CITY: "City",
  COUNTRY: "Country",
  CURRENCY: "Currency",
  ID_SOURCE: "ID in the source system",
  ID_EDITED: "ID edited in Management",
  PARENT_ID: "Parent ID in the CBC",
  PARENT_TYPE: "Parent type",
  PARENT_NAME: "Parent name",
  PARENT_ID_SOURCE: "Parent ID in the source system",
  PARENT_ID_EDITED: "Parent ID edited in Management",
  COMPANY_ID: "Company ID",
  COMPANY_ID_EDITED: "Company ID edited in Management",
  COMPANY_NAME: "Company name",
  COMPANY_COUNTRY: "Company country",
  DISTRIBUTION_CHANNEL: "Distribution Channel",
  DIVISION: "Division",
  ID_RULE: "ID rule",
  COMPANY_ID_RULE: "Company ID rule",
};

/** `col:UNIT.ID` → "Unit ID"; coluna de outra tabela → `TABELA.CAMPO` (sem o `col:`); `const:x` →
 *  o nome da constante em palavras. Texto que não é referência volta como veio. */
export function columnLabel(t: TFunction, reference: string): string {
  const column = /^col:([A-Z0-9_]+)\.([A-Z0-9_]+)$/.exec(reference.trim());
  if (column) {
    const [, table, field] = column;
    if (table === "UNIT" && UNIT_COLUMN_LABELS[field]) return t(UNIT_COLUMN_LABELS[field]);
    return `${table}.${field}`;
  }
  const constant = /^const:([a-z0-9_]+)$/.exec(reference.trim());
  if (constant) return constant[1].replace(/_/g, " ");
  return reference;
}

/** O texto do chat sem as referências técnicas que a LLM às vezes escreve (`col:UNIT.ID`). */
export function friendlyRefs(t: TFunction, text: string): string {
  return text.replace(/\b(?:col:[A-Z0-9_]+\.[A-Z0-9_]+|const:[a-z0-9_]+)/g, (match) =>
    columnLabel(t, match),
  );
}
